import { ApiService, ApiResponse } from './api';
import { Product } from '../types';

export interface ProductFilters {
  search?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  rating?: number;
  tags?: string[];
  author?: string;
  sortBy?: 'newest' | 'price-low' | 'price-high' | 'rating' | 'popularity';
  page?: number;
  limit?: number;
}

export interface ProductSearchResponse {
  products: Product[];
  total: number;
  page: number;
  totalPages: number;
  hasMore: boolean;
}

export interface ProductStats {
  totalProducts: number;
  totalDownloads: number;
  averageRating: number;
  categories: {
    name: string;
    count: number;
  }[];
}

export class ProductService extends ApiService {
  // Get all products with optional filtering
  async getProducts(filters?: ProductFilters): Promise<ApiResponse<ProductSearchResponse>> {
    return this.get<ProductSearchResponse>('/products', filters);
  }

  // Get a single product by ID
  async getProduct(id: string): Promise<ApiResponse<Product>> {
    // Remove query parameters from id if present (for cache busting)
    const cleanId = id.split('?')[0];
    return this.get<Product>(`/products/${cleanId}`);
  }

  // Get featured products
  async getFeaturedProducts(limit: number = 6): Promise<ApiResponse<Product[]>> {
    return this.get<Product[]>('/products/featured', { limit });
  }

  // Get trending products
  async getTrendingProducts(limit: number = 6): Promise<ApiResponse<Product[]>> {
    return this.get<Product[]>('/products/trending', { limit });
  }

  // Get products by category
  async getProductsByCategory(category: string, filters?: Omit<ProductFilters, 'category'>): Promise<ApiResponse<ProductSearchResponse>> {
    return this.get<ProductSearchResponse>(`/products/category/${category}`, filters);
  }

  // Get products by author
  async getProductsByAuthor(authorId: string, filters?: Omit<ProductFilters, 'author'>): Promise<ApiResponse<ProductSearchResponse>> {
    return this.get<ProductSearchResponse>(`/products/author/${authorId}`, filters);
  }

  // Search products
  async searchProducts(query: string, filters?: Omit<ProductFilters, 'search'>): Promise<ApiResponse<ProductSearchResponse>> {
    return this.get<ProductSearchResponse>('/products/search', { search: query, ...filters });
  }

  // Get product statistics
  async getProductStats(): Promise<ApiResponse<ProductStats>> {
    return this.get<ProductStats>('/products/stats');
  }

  // Get related products
  async getRelatedProducts(productId: string, limit: number = 4): Promise<ApiResponse<Product[]>> {
    return this.get<Product[]>(`/products/${productId}/related`, { limit });
  }

  // Get product categories
  async getCategories(): Promise<ApiResponse<{ name: string; count: number; }[]>> {
    return this.get<{ name: string; count: number; }[]>('/products/categories');
  }

  // Get popular tags
  async getPopularTags(limit: number = 20): Promise<ApiResponse<string[]>> {
    return this.get<string[]>('/products/tags/popular', { limit });
  }

  // Rate a product
  async rateProduct(productId: string, rating: number, review?: string): Promise<ApiResponse<void>> {
    return this.post<void>(`/products/${productId}/rate`, { rating, review });
  }

  // Report a product
  async reportProduct(productId: string, reason: string, description?: string): Promise<ApiResponse<void>> {
    return this.post<void>(`/products/${productId}/report`, { reason, description });
  }

  // Add product to favorites
  async addToFavorites(productId: string): Promise<ApiResponse<void>> {
    return this.post<void>(`/products/${productId}/favorite`);
  }

  // Remove product from favorites
  async removeFromFavorites(productId: string): Promise<ApiResponse<void>> {
    return this.delete<void>(`/products/${productId}/favorite`);
  }

  // Get user's favorite products
  async getFavorites(filters?: Omit<ProductFilters, 'page' | 'limit'>): Promise<ApiResponse<ProductSearchResponse>> {
    return this.get<ProductSearchResponse>('/products/favorites', filters);
  }

  // Check if product is in user's favorites
  async isFavorite(productId: string): Promise<ApiResponse<boolean>> {
    return this.get<boolean>(`/products/${productId}/favorite`);
  }

  // Get reviews for a product
  async getReviews(productId: string): Promise<ApiResponse<{
    reviews: Array<{
      id: string;
      rating: number;
      text: string;
      reviewerName: string;
      reviewerEmail: string;
      avatar: string | null;
      date: string;
      itemQuality?: number;
      shipping?: number;
      customerService?: number;
      helpful?: number;
      verifiedPurchase?: boolean;
    }>;
    averageRating: string;
    avgItemQuality: string;
    avgShipping: string;
    avgCustomerService: string;
    totalReviews: number;
  }>> {
    return this.get<{
      reviews: Array<{
        id: string;
        rating: number;
        text: string;
        reviewerName: string;
        reviewerEmail: string;
        avatar: string | null;
        date: string;
        itemQuality?: number;
        shipping?: number;
        customerService?: number;
        helpful?: number;
        verifiedPurchase?: boolean;
      }>;
      averageRating: string;
      avgItemQuality: string;
      avgShipping: string;
      avgCustomerService: string;
      totalReviews: number;
    }>(`/products/${productId}/reviews`);
  }

  // Create a review for a product
  async createReview(productId: string, rating: number, text: string, itemQuality?: number, shipping?: number, customerService?: number): Promise<ApiResponse<{
    id: string;
    rating: number;
    text: string;
    reviewerName: string;
    reviewerEmail: string;
    avatar: string | null;
    date: string;
    itemQuality?: number;
    shipping?: number;
    customerService?: number;
    verifiedPurchase?: boolean;
  }>> {
    return this.post<{
      id: string;
      rating: number;
      text: string;
      reviewerName: string;
      reviewerEmail: string;
      avatar: string | null;
      date: string;
      itemQuality?: number;
      shipping?: number;
      customerService?: number;
      verifiedPurchase?: boolean;
    }>(`/products/${productId}/reviews`, {
      rating,
      text,
      itemQuality,
      shipping,
      customerService
    });
  }

  // Get product download history
  async getDownloadHistory(productId: string): Promise<ApiResponse<{
    downloads: number;
    lastDownload?: string;
    downloadUrl?: string;
  }>> {
    return this.get<{
      downloads: number;
      lastDownload?: string;
      downloadUrl?: string;
    }>(`/products/${productId}/downloads`);
  }

  // Request product download
  async requestDownload(productId: string): Promise<ApiResponse<{
    downloadUrl: string;
    expiresAt: string;
  }>> {
    return this.post<{
      downloadUrl: string;
      expiresAt: string;
    }>(`/products/${productId}/download`);
  }

  // Get product analytics (for authors)
  async getProductAnalytics(productId: string, period: 'day' | 'week' | 'month' | 'year' = 'month'): Promise<ApiResponse<{
    views: number;
    downloads: number;
    revenue: number;
    ratings: {
      average: number;
      count: number;
      distribution: Record<number, number>;
    };
  }>> {
    return this.get<{
      views: number;
      downloads: number;
      revenue: number;
      ratings: {
        average: number;
        count: number;
        distribution: Record<number, number>;
      };
    }>(`/products/${productId}/analytics`, { period });
  }

  // Initialize presigned upload - get presigned URLs for direct S3 uploads
  async initPresignedUpload(productData: {
    name: string;
    description: string;
    price: string | number;
    category: string;
    tags?: string;
    previewImages: Array<{ name: string; size: number; type: string }>;
    downloadFile: { name: string; size: number; type: string };
  }): Promise<ApiResponse<{
    productId: string;
    presignedUrls: {
      previewImages: Array<{
        key: string;
        url: string;
        originalName: string;
        contentType: string;
      }>;
      downloadFile: {
        key: string;
        url: string;
        originalName: string;
        contentType: string;
      };
    };
    expiresIn: number;
  }>> {
    return this.post<{
      productId: string;
      presignedUrls: {
        previewImages: Array<{
          key: string;
          url: string;
          originalName: string;
          contentType: string;
        }>;
        downloadFile: {
          key: string;
          url: string;
          originalName: string;
          contentType: string;
        };
      };
      expiresIn: number;
    }>('/products/presigned-upload/init', productData);
  }

  // Finalize presigned upload - verify files uploaded and create product
  async finalizePresignedUpload(productId: string, uploadedFiles: {
    previewImages: Array<{ key: string; originalName: string }>;
    downloadFile: { key: string; originalName: string };
  }): Promise<ApiResponse<{
    id: string;
    _id: string;
    name: string;
    s3Key: string;
    previewImagesCount: number;
  }>> {
    return this.post<{
      id: string;
      _id: string;
      name: string;
      s3Key: string;
      previewImagesCount: number;
    }>('/products/presigned-upload/finalize', {
      productId,
      uploadedFiles
    });
  }

  // Upload file directly to S3 using presigned URL
  async uploadFileToS3(presignedUrl: string, file: File, onProgress?: (progress: { loaded: number; total: number }) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();

      // Track upload progress
      if (onProgress) {
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            onProgress({
              loaded: event.loaded,
              total: event.total
            });
          }
        });
      }

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          // Check for CORS errors
          if (xhr.status === 0 || (xhr.status >= 400 && !xhr.responseText)) {
            reject(new Error('CORS error: S3 bucket is not configured for cross-origin uploads. Please configure CORS on your S3 bucket. See S3_CORS_SETUP.md for instructions.'));
          } else {
            reject(new Error(`Upload failed: ${xhr.statusText} (${xhr.status})`));
          }
        }
      });

      xhr.addEventListener('error', () => {
        // CORS errors typically result in status 0 and no response
        if (xhr.status === 0 && !xhr.responseText) {
          reject(new Error('CORS error: S3 bucket is not configured for cross-origin uploads. Please configure CORS on your S3 bucket. See S3_CORS_SETUP.md for instructions.'));
        } else {
          reject(new Error('Network error during upload'));
        }
      });

      xhr.addEventListener('abort', () => {
        reject(new Error('Upload aborted'));
      });

      xhr.open('PUT', presignedUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      // Don't set additional headers - presigned URLs handle authentication
      // The browser will automatically handle CORS preflight if needed
      xhr.send(file);
    });
  }

  // Update product (admin only)
  async updateProduct(productId: string, updateData: Partial<Product>): Promise<ApiResponse<Product>> {
    return this.put<Product>(`/products/${productId}`, updateData);
  }

  // Update product with files (admin only)
  async updateProductWithFiles(productId: string, formData: FormData): Promise<ApiResponse<Product>> {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:256',message:'updateProductWithFiles entry',data:{productId,formDataKeys:Array.from(formData.keys())},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'})}).catch(()=>{});
    // #endregion
    const token = localStorage.getItem('authToken');
    if (!token) {
      throw new Error('Authentication required. Please log in again.');
    }

    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:265',message:'Before fetch request',data:{url:`${API_BASE_URL}/products/${productId}`,hasToken:!!token,tokenLength:token.length},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'})}).catch(()=>{});
    // #endregion
    
    try {
      const response = await fetch(`${API_BASE_URL}/products/${productId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type - browser will set it with boundary for FormData
        },
        body: formData
      });

      // #region agent log
      console.log('[DEBUG] Fetch response received:', { status: response.status, statusText: response.statusText, ok: response.ok });
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:274',message:'Fetch response received',data:{status:response.status,statusText:response.statusText,ok:response.ok},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D,F'})}).catch(()=>{});
      // #endregion

      const data = await response.json();
      
      // #region agent log
      console.log('[DEBUG] Response data parsed:', { success: data.success, message: data.message, error: data.error });
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:277',message:'Response data parsed',data:{success:data.success,message:data.message,error:data.error},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      
      if (!response.ok) {
        // Handle different error response formats
        const errorMessage = data.message || data.error || `Failed to update product (${response.status})`;
        
        // Provide more specific error messages
        if (response.status === 401) {
          throw new Error('Your session has expired. Please log in again.');
        } else if (response.status === 403) {
          throw new Error('You do not have permission to edit products. Admin access required.');
        } else if (response.status === 404) {
          throw new Error('Product not found. It may have been deleted.');
        }
        
        throw new Error(errorMessage);
      }

      return {
        success: true,
        data: data.data || data,
        message: data.message || 'Product updated successfully'
      };
    } catch (error: any) {
      // Re-throw if it's already an Error with a message
      if (error instanceof Error) {
        throw error;
      }
      // Otherwise wrap in Error
      throw new Error(error.message || 'Failed to update product. Please try again.');
    }
  }

  // Delete product (admin only)
  async deleteProduct(productId: string): Promise<ApiResponse<void>> {
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:323',message:'deleteProduct entry',data:{productId},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A,E'})}).catch(()=>{});
    // #endregion
    const token = localStorage.getItem('authToken');
    // #region agent log
    fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:325',message:'Delete token check',data:{hasToken:!!token},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'A'})}).catch(()=>{});
    // #endregion
    try {
      const result = await this.delete<void>(`/products/${productId}`);
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:329',message:'Delete API success',data:{success:result.success},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'F'})}).catch(()=>{});
      // #endregion
      return result;
    } catch (error: any) {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/508c2b0f-0288-4388-a63c-b8d241946ac0',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({location:'productService.ts:delete-error',message:'Delete API error',data:{errorMessage:error.message,errorStatus:error.status},timestamp:Date.now(),sessionId:'debug-session',runId:'run1',hypothesisId:'D,F'})}).catch(()=>{});
      // #endregion
      throw error;
    }
  }
}

// Export singleton instance
export const productService = new ProductService(); 